<template>
  <article class="question-card">
    <div class="question-meta">
      <span class="drag-handle" :title="$t('form.questions.reorder_hint')" aria-hidden="true">
        <IconGripVertical :size="16" />
      </span>
      <span class="question-number">{{ String(position).padStart(2, '0') }}</span>
      <span class="question-type">{{ $t(`form.questions.type_${question.type}`) }}</span>
      <div class="question-actions">
        <LeadButton variant="icon" :aria-label="$t('form.questions.edit_question')" @click="$emit('edit')">
          <IconPencil :size="16" />
        </LeadButton>
        <LeadButton
          variant="icon"
          class="delete-button"
          :aria-label="$t('form.questions.delete_question')"
          @click="$emit('delete')">
          <IconTrash :size="16" />
        </LeadButton>
      </div>
    </div>
    <LeadButton variant="white" class="question-title" @click="$emit('edit')">
      {{ question.title }}
    </LeadButton>
    <div v-if="question.type === 'text'" class="answer-hint">
      <IconAlignLeft :size="15" />
      {{ $t('form.questions.free_text') }}
    </div>
    <ul v-else-if="question.answers?.length" class="answer-preview">
      <li v-for="(answer, index) in question.answers" :key="index">
        <span class="answer-marker" :class="{ square: question.type === 'multiple' }" aria-hidden="true" />
        {{ answer.label }}
      </li>
    </ul>
    <p v-else class="answer-hint">{{ $t('form.questions.no_answers') }}</p>
  </article>
</template>

<script setup lang="ts">
import { IconGripVertical, IconTrash, IconPencil, IconAlignLeft } from '@tabler/icons-vue';
import type { UiQuestion } from '@/types/formQuestion';
import LeadButton from '../generic/LeadButton.vue';

interface Props {
  question: UiQuestion;
  position: number;
}

defineProps<Props>();
defineEmits<{ edit: []; delete: [] }>();
</script>

<style scoped>
.question-card {
  padding: 16px;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgb(16 24 40 / 3%);
  transition: border-color 0.15s;
}

.question-card:hover,
.question-card:focus-within {
  border-color: #b9c2d0;
}

.question-meta,
.question-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.question-meta {
  margin-bottom: 10px;
}

.drag-handle {
  display: inline-flex;
  cursor: grab;
  color: #98a2b3;
  touch-action: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.question-number {
  color: #667085;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.question-type {
  padding: 3px 7px;
  border-radius: 5px;
  color: #475467;
  background: #f2f4f7;
  font-size: 11px;
  font-weight: 500;
}

.question-actions {
  margin-left: auto;
  gap: 2px;
}

.question-actions .lead-button {
  height: 28px;
  width: 28px;
  padding: 5px;
  border: 0;
  box-shadow: none;
  color: #667085;
}

.question-actions .delete-button:hover {
  background: #fef3f2;
  color: #d92d20;
}

.question-title.lead-button {
  display: block;
  width: 100%;
  height: auto;
  padding: 0;
  margin-bottom: 12px;
  border: 0;
  background: transparent;
  box-shadow: none;
  color: #101828;
  font-size: 14px;
  line-height: 1.5;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
}

.question-title :deep(.slot-wrapper) {
  display: block;
}

.question-title:hover {
  color: var(--tblr-primary);
}

.lead-button:focus-visible {
  outline: 2px solid var(--tblr-primary);
  outline-offset: 3px;
}

.answer-preview {
  display: grid;
  gap: 7px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.answer-preview li,
.answer-hint {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.answer-marker {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border: 1px solid #c4cbd5;
  border-radius: 50%;
}

.answer-marker.square {
  border-radius: 2px;
}
</style>
